/** Product-issued customer invitation via tmm-api (TMMPDM-0013). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AdminInvitationResponse } from '@/api/tmm/types';

interface InviteCustomerDialogProps {
  open: boolean;
  slug: string;
  onClose: () => void;
  onDone: (result: AdminInvitationResponse) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteCustomerDialog({ open, slug, onClose, onDone }: InviteCustomerDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <InviteCustomerForm key={slug} slug={slug} onClose={onClose} onDone={onDone} />
      ) : null}
    </Dialog>
  );
}

function InviteCustomerForm({ slug, onClose, onDone }: Omit<InviteCustomerDialogProps, 'open'>) {
  const { t } = useCopy();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    const nextFields: Record<string, string> = {};
    const trimmed = email.trim();
    if (!trimmed) {
      nextFields.email = t('customers.invite.emailRequired', 'Email is required.');
    } else if (!EMAIL_RE.test(trimmed)) {
      nextFields.email = t('customers.invite.emailInvalid', 'Enter a valid email.');
    }
    if (Object.keys(nextFields).length > 0) {
      setFieldErrors(nextFields);
      return;
    }
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      onDone(await adminApi.inviteCustomer(slug, { email: trimmed }));
    } catch (error: unknown) {
      if (error instanceof ApiError && isValidationFailed(error)) {
        const fields = validationFields(error, t);
        setFieldErrors(fields);
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((field) => field !== 'email')) {
          setFormError(
            errorCopy(error, t, t('customers.invite.error', 'Could not invite customer.')),
          );
        }
      } else {
        setFormError(
          errorCopy(error, t, t('customers.invite.error', 'Could not invite customer.')),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('customers.invite.title', 'Invite customer')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('customers.invite.email', 'Email')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('customers.invite.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('customers.invite.submit', 'Invite')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
